import React, { useState, useEffect, useMemo } from 'react';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Download, 
  RefreshCw, 
  Sparkles, 
  Eye, 
  Send, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Layers, 
  ExternalLink,
  Plus,
  X,
  TrendingUp,
  Mail,
  MousePointer,
  Filter
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { API_BASE_URL } from '../config';

interface EspColumn {
  id: string;
  label: string;
  shortName: string;
  domain: string;
  defaultTimes: string[];
  color: string;
}

interface CampaignSlot {
  afimaCampaignId: string;
  campaignName: string;
  textEdition: string;
  scheduleTime: string;
  status: 'sent' | 'scheduled' | 'pending' | 'approved';
  subject: string;
  previewText: string;
  delivered: number;
  uniqueOpens: number;
  uniqueClicks: number;
  unsubs: number;
  openRate: number;
  clickRate: number;
  includeNL: boolean;
  hasHtml: boolean;
  advertiser: string;
}

interface MatrixDayRow {
  date: string;
  formattedDate: string;
  dayName: string;
  weekday: number;
  weekId: string;
  isToday: boolean;
  isPast: boolean;
  isFuture: boolean;
  slots: Record<string, CampaignSlot[]>;
}

interface MatrixKpis {
  totalSent: number;
  totalDelivered: number;
  totalOpens: number;
  totalClicks: number;
  totalUnsubs: number;
  avgOpenRate: number;
  scheduledCount: number;
  sentCount: number;
}

interface CandidateCampaign {
  campaignId: string;
  brandName: string;
  category: string;
  affiliateSlug: string;
  editions: string[];
}

export default function DispatchMatrix() {
  const [activeTab, setActiveTab] = useState<'se' | 'no' | 'fi' | 'de' | 'simplyloan'>('se');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Date window state (default to 4 weeks centered on today)
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Data state
  const [columns, setColumns] = useState<EspColumn[]>([]);
  const [rows, setRows] = useState<MatrixDayRow[]>([]);
  const [kpis, setKpis] = useState<MatrixKpis>({
    totalSent: 0,
    totalDelivered: 0,
    totalOpens: 0,
    totalClicks: 0,
    totalUnsubs: 0,
    avgOpenRate: 0,
    scheduledCount: 0,
    sentCount: 0
  });
  const [candidates, setCandidates] = useState<CandidateCampaign[]>([]);

  // Preview Modal state
  const [previewCampaignId, setPreviewCampaignId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<any | null>(null);

  // Add/Swap Slot Modal state
  const [slotModalOpen, setSlotModalOpen] = useState<boolean>(false);
  const [slotModalData, setSlotModalData] = useState<{
    date: string;
    espId: string;
    espName: string;
    existingSlot?: CampaignSlot;
  } | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedEdition, setSelectedEdition] = useState<string>('TA1');
  const [selectedTime, setSelectedTime] = useState<string>('09:00');
  const [savingSlot, setSavingSlot] = useState<boolean>(false);

  // Calculate start and end date based on weekOffset
  const dateRange = useMemo(() => {
    const now = new Date();
    const currentMonday = new Date(now);
    const day = currentMonday.getDay();
    const diff = currentMonday.getDate() - day + (day === 0 ? -6 : 1);
    currentMonday.setDate(diff + (weekOffset * 7));
    currentMonday.setHours(0, 0, 0, 0);

    const startDt = new Date(currentMonday);
    startDt.setDate(startDt.getDate() - 7); // Include previous week for context

    const endDt = new Date(currentMonday);
    endDt.setDate(endDt.getDate() + 20); // 3 full weeks ahead

    const formatDate = (d: Date) => d.toISOString().split('T')[0];
    return {
      start: formatDate(startDt),
      end: formatDate(endDt)
    };
  }, [weekOffset]);

  // Fetch Matrix Data
  const fetchMatrix = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE_URL}/api/dispatch-matrix?tab=${activeTab}&startDate=${dateRange.start}&endDate=${dateRange.end}&search=${encodeURIComponent(searchQuery)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const data = await res.json();
      
      setColumns(data.columns || []);
      setRows(data.rows || []);
      setKpis(data.kpis || {
        totalSent: 0,
        totalDelivered: 0,
        totalOpens: 0,
        totalClicks: 0,
        totalUnsubs: 0,
        avgOpenRate: 0,
        scheduledCount: 0,
        sentCount: 0
      });
      setCandidates(data.candidates || []);
    } catch (err: any) {
      console.error('[DispatchMatrix] Fetch error:', err);
      setError(err.message || 'Failed to load dispatch matrix data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatrix();
  }, [activeTab, dateRange, searchQuery]);

  // Fetch Campaign Preview
  const handleOpenPreview = async (afimaCampaignId: string) => {
    if (!afimaCampaignId) return;
    setPreviewCampaignId(afimaCampaignId);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/dispatch-matrix/preview?afimaCampaignId=${afimaCampaignId}`);
      if (!res.ok) throw new Error(`Preview error ${res.status}`);
      const data = await res.json();
      setPreviewData(data);
    } catch (err: any) {
      console.error('[DispatchMatrix] Preview fetch error:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Open Add/Edit Slot Modal
  const handleOpenSlotModal = (date: string, espId: string, espName: string, existingSlot?: CampaignSlot) => {
    setSlotModalData({ date, espId, espName, existingSlot });
    if (existingSlot) {
      setSelectedBrand(existingSlot.advertiser || existingSlot.campaignName.split(' ')[0]);
      setSelectedEdition(existingSlot.textEdition || 'TA1');
      setSelectedTime(existingSlot.scheduleTime || '09:00');
    } else {
      setSelectedBrand(candidates[0]?.brandName || '');
      setSelectedEdition('TA1');
      setSelectedTime('09:00');
    }
    setSlotModalOpen(true);
  };

  // Save Slot
  const handleSaveSlot = async () => {
    if (!slotModalData || !selectedBrand) return;
    setSavingSlot(true);
    try {
      const payload = {
        espId: slotModalData.espId,
        scheduledDate: slotModalData.date,
        campaignName: `${selectedBrand} ${selectedEdition}`,
        textEdition: selectedEdition,
        scheduleTime: selectedTime,
        status: 'approved'
      };
      
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/add-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) throw new Error(`Save failed ${res.status}`);
      setSlotModalOpen(false);
      fetchMatrix();
    } catch (err: any) {
      alert(`Error saving slot: ${err.message}`);
    } finally {
      setSavingSlot(false);
    }
  };

  // Export matrix to CSV (matching old Google Sheet structure)
  const handleExportCSV = () => {
    if (!rows.length || !columns.length) return;
    
    // Header row
    const headers = ['Day', 'Date'];
    columns.forEach(col => {
      headers.push(col.label, 'Time / Stats');
    });
    
    const csvLines = [headers.join(',')];
    
    rows.forEach(r => {
      const line = [`"${r.dayName}"`, `"${r.formattedDate}"`];
      columns.forEach(col => {
        const slots = r.slots[col.id] || [];
        if (slots.length === 0) {
          line.push('""', '""');
        } else {
          const names = slots.map(s => `${s.campaignName}${s.includeNL ? '+NL' : ''}`).join(' / ');
          const times = slots.map(s => {
            if (s.status === 'sent') {
              return `${s.scheduleTime} (${s.openRate}% OR, ${s.uniqueClicks} clicks)`;
            }
            return s.scheduleTime;
          }).join(' / ');
          line.push(`"${names}"`, `"${times}"`);
        }
      });
      csvLines.push(line.join(','));
    });
    
    const csvContent = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvLines.join('\n'));
    const link = document.createElement('a');
    link.setAttribute('href', csvContent);
    link.setAttribute('download', `Campaign_Sendout_Matrix_${activeTab.toUpperCase()}_${dateRange.start}_to_${dateRange.end}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full pb-16">
        
        {/* Top Header & Title */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
                <Layers className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Campaign Sendout Matrix</h1>
                <p className="text-sm text-gray-500">Live multi-ESP dispatch tracking, sent campaign metrics, and scheduled queue planner</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              onClick={() => setWeekOffset(0)}
              className={`px-3.5 py-2 text-xs font-semibold rounded-lg border transition-all ${
                weekOffset === 0 
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Current Week
            </button>
            <div className="flex items-center bg-gray-50 border border-gray-300 rounded-lg p-0.5">
              <button
                onClick={() => setWeekOffset(prev => prev - 1)}
                className="p-1.5 hover:bg-white rounded-md text-gray-600 transition-colors"
                title="Previous 2 Weeks"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2.5 text-xs font-medium text-gray-600">
                {weekOffset === 0 ? 'This Period' : `${weekOffset > 0 ? '+' : ''}${weekOffset} Weeks`}
              </span>
              <button
                onClick={() => setWeekOffset(prev => prev + 1)}
                className="p-1.5 hover:bg-white rounded-md text-gray-600 transition-colors"
                title="Next 2 Weeks"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-all"
            >
              <Download className="w-3.5 h-3.5 text-gray-500" />
              Export CSV
            </button>

            <button
              onClick={fetchMatrix}
              disabled={loading}
              className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tab Navigation Bar & Search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-200">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
            {[
              { id: 'se', label: '🇸🇪 Sweden (SE)', count: '6 ESPs' },
              { id: 'no', label: '🇳🇴 Norway (NO)', count: '3 ESPs' },
              { id: 'fi', label: '🇫🇮 Finland (FI)', count: '5 ESPs' },
              { id: 'de', label: '🇩🇪 Germany (DE)', count: '2 ESPs' },
              { id: 'simplyloan', label: '💼 SimplyLoan (Dedicated)', count: '3 Geos' }
            ].map(tab => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
                    active
                      ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    active ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Quick Search */}
          <div className="relative w-full md:w-64 pb-2 md:pb-0">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search campaigns or offers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Live KPI Ribbon */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
          <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-gray-500 text-xs font-medium">
              <span>Total Delivered</span>
              <Send className="w-3.5 h-3.5 text-indigo-500" />
            </div>
            <div className="text-xl font-bold text-gray-900 mt-1">
              {kpis.totalDelivered.toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">{kpis.sentCount} dispatches sent</div>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-gray-500 text-xs font-medium">
              <span>Unique Opens</span>
              <Eye className="w-3.5 h-3.5 text-emerald-500" />
            </div>
            <div className="text-xl font-bold text-emerald-600 mt-1">
              {kpis.totalOpens.toLocaleString()}
            </div>
            <div className="text-[11px] text-emerald-600/80 font-medium mt-0.5">{kpis.avgOpenRate}% avg open rate</div>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-gray-500 text-xs font-medium">
              <span>Unique Clicks</span>
              <MousePointer className="w-3.5 h-3.5 text-blue-500" />
            </div>
            <div className="text-xl font-bold text-blue-600 mt-1">
              {kpis.totalClicks.toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              {kpis.totalDelivered > 0 ? ((kpis.totalClicks / kpis.totalDelivered) * 100).toFixed(2) : '0.00'}% CTR
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-gray-500 text-xs font-medium">
              <span>Unsubscribes</span>
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <div className="text-xl font-bold text-gray-900 mt-1">
              {kpis.totalUnsubs.toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              {kpis.totalDelivered > 0 ? ((kpis.totalUnsubs / kpis.totalDelivered) * 100).toFixed(2) : '0.00'}% unsub rate
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-gray-500 text-xs font-medium">
              <span>Scheduled Ahead</span>
              <Clock className="w-3.5 h-3.5 text-purple-500" />
            </div>
            <div className="text-xl font-bold text-purple-600 mt-1">
              {kpis.scheduledCount} slots
            </div>
            <div className="text-[11px] text-purple-600/80 font-medium mt-0.5">Ready in queue</div>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-gray-500 text-xs font-medium">
              <span>Active Mailers</span>
              <Layers className="w-3.5 h-3.5 text-teal-500" />
            </div>
            <div className="text-xl font-bold text-gray-900 mt-1">
              {columns.length} ESPs
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">{activeTab.toUpperCase()} Infrastructure</div>
          </div>
        </div>

        {/* Master Matrix Table Container */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-16 flex flex-col items-center justify-center text-center">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
              <p className="text-sm font-semibold text-gray-800">Loading Dispatch Matrix...</p>
              <p className="text-xs text-gray-400 mt-1">Aggregating live sent metrics and scheduled queues</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-800">{error}</p>
              <button
                onClick={fetchMatrix}
                className="mt-3 px-4 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[75vh] scrollbar-thin">
              <table className="w-full border-collapse text-left">
                {/* Table Sticky Header */}
                <thead className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
                  <tr>
                    <th className="sticky left-0 z-30 bg-gray-100/95 backdrop-blur-sm px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[140px] border-r border-gray-200">
                      Date & Day
                    </th>
                    {columns.map(col => (
                      <th key={col.id} className="px-4 py-3 text-xs font-bold text-gray-700 min-w-[230px] border-r border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900">{col.label}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-mono">
                            {col.domain}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-400 font-normal mt-0.5">
                          Times: {col.defaultTimes.join(' / ')}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody className="divide-y divide-gray-200 text-xs">
                  {rows.map((row, rowIdx) => {
                    // Week separator logic
                    const isNewWeek = rowIdx === 0 || rows[rowIdx - 1].weekId !== row.weekId;
                    
                    return (
                      <React.Fragment key={row.date}>
                        {isNewWeek && (
                          <tr className="bg-slate-100/80 border-y border-slate-200">
                            <td colSpan={columns.length + 1} className="px-4 py-1.5 text-xs font-bold text-slate-700 tracking-wide">
                              🗓️ {row.weekId}
                            </td>
                          </tr>
                        )}
                        <tr className={`hover:bg-gray-50/60 transition-colors ${row.isToday ? 'bg-indigo-50/30' : ''}`}>
                          
                          {/* Sticky Left Date Cell */}
                          <td className={`sticky left-0 z-10 px-4 py-3 border-r border-gray-200 font-medium ${
                            row.isToday ? 'bg-indigo-50/95 text-indigo-900' : 'bg-white text-gray-800'
                          }`}>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-900">{row.dayName}</span>
                              {row.isToday && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-600 text-white uppercase tracking-wider">
                                  Today
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-500 font-mono mt-0.5">
                              {row.formattedDate}
                            </div>
                          </td>

                          {/* Columns for each ESP */}
                          {columns.map(col => {
                            const slots = row.slots[col.id] || [];
                            
                            return (
                              <td key={col.id} className="p-2 border-r border-gray-200 align-top">
                                {slots.length === 0 ? (
                                  /* Empty slot placeholder */
                                  <button
                                    onClick={() => handleOpenSlotModal(row.date, col.id, col.label)}
                                    className="w-full h-14 rounded-lg border border-dashed border-gray-200 hover:border-indigo-400 hover:bg-indigo-50/40 text-gray-300 hover:text-indigo-600 flex items-center justify-center gap-1 transition-all group"
                                  >
                                    <Plus className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                                    <span className="text-[11px] font-medium hidden group-hover:inline">Add Slot</span>
                                  </button>
                                ) : (
                                  /* List of scheduled / sent campaign cards */
                                  <div className="space-y-1.5">
                                    {slots.map((slot, sIdx) => {
                                      const isSent = slot.status === 'sent';
                                      
                                      return (
                                        <div
                                          key={sIdx}
                                          onClick={() => handleOpenPreview(slot.afimaCampaignId)}
                                          className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all shadow-xs hover:shadow-md ${
                                            isSent
                                              ? 'bg-emerald-50/40 border-emerald-200 hover:border-emerald-400'
                                              : 'bg-indigo-50/40 border-indigo-200 hover:border-indigo-400'
                                          }`}
                                        >
                                          {/* Card Header: Time & Edition */}
                                          <div className="flex items-center justify-between text-[11px] mb-1">
                                            <span className="font-bold text-gray-700 flex items-center gap-1 font-mono">
                                              <Clock className="w-3 h-3 text-gray-400" />
                                              {slot.scheduleTime}
                                            </span>
                                            <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${
                                              isSent ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'
                                            }`}>
                                              {slot.textEdition}
                                            </span>
                                          </div>

                                          {/* Campaign Name */}
                                          <div className="font-semibold text-gray-900 text-xs truncate" title={slot.campaignName}>
                                            {slot.campaignName}
                                            {slot.includeNL && <span className="ml-1 text-[10px] text-purple-600 font-bold">+NL</span>}
                                          </div>

                                          {/* Metrics or Status */}
                                          {isSent ? (
                                            <div className="mt-1.5 pt-1.5 border-t border-emerald-200/60 flex items-center justify-between text-[10px]">
                                              <span className="font-bold text-emerald-700">
                                                {slot.openRate}% OR
                                              </span>
                                              <span className="text-gray-500 font-medium">
                                                {slot.uniqueClicks} clicks
                                              </span>
                                              <span className="text-gray-400">
                                                {(slot.delivered / 1000).toFixed(0)}k sent
                                              </span>
                                            </div>
                                          ) : (
                                            <div className="mt-1.5 pt-1.5 border-t border-indigo-200/60 flex items-center justify-between text-[10px]">
                                              <span className="text-indigo-600 font-medium flex items-center gap-0.5">
                                                <CheckCircle2 className="w-2.5 h-2.5" />
                                                Queued
                                              </span>
                                              <span className="text-gray-400 hover:text-indigo-600 flex items-center gap-0.5">
                                                <Eye className="w-2.5 h-2.5" />
                                                Preview
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* ─── MODAL 1: Email Preview & Inspection Slide-over ─── */}
      {previewCampaignId && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/50 backdrop-blur-xs flex justify-end transition-all">
          <div className="w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">{previewData?.campaignName || 'Campaign Details'}</h3>
                  <p className="text-xs text-gray-500">{previewData?.scheduleDate} • {previewData?.scheduleTime} • {previewData?.systemId}</p>
                </div>
              </div>
              <button
                onClick={() => setPreviewCampaignId(null)}
                className="p-1.5 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            {previewLoading ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <RefreshCw className="w-6 h-6 text-indigo-600 animate-spin mb-2" />
                <p className="text-xs text-gray-500 font-medium">Fetching email preview & performance stats...</p>
              </div>
            ) : previewData ? (
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                
                {/* Meta details */}
                <div className="bg-gray-50 p-3.5 rounded-lg border border-gray-200 space-y-2 text-xs">
                  <div>
                    <span className="font-semibold text-gray-500">Subject Line:</span>
                    <p className="font-medium text-gray-900 mt-0.5">{previewData.subject || '(No subject line)'}</p>
                  </div>
                  {previewData.previewText && (
                    <div>
                      <span className="font-semibold text-gray-500">Preview Text:</span>
                      <p className="text-gray-700 mt-0.5">{previewData.previewText}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-4 pt-1 text-[11px] text-gray-600">
                    <span>Edition: <strong>{previewData.textEdition}</strong></span>
                    <span>Status: <strong className="uppercase">{previewData.status}</strong></span>
                  </div>
                </div>

                {/* Performance stats if sent */}
                {previewData.stats?.delivered > 0 && (
                  <div className="grid grid-cols-4 gap-2 bg-emerald-50/60 border border-emerald-200 p-3 rounded-lg text-center">
                    <div>
                      <div className="text-[10px] text-emerald-800 font-semibold uppercase">Delivered</div>
                      <div className="text-sm font-bold text-gray-900">{previewData.stats.delivered.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-emerald-800 font-semibold uppercase">Opens</div>
                      <div className="text-sm font-bold text-emerald-600">{previewData.stats.opens.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-emerald-800 font-semibold uppercase">Open Rate</div>
                      <div className="text-sm font-bold text-emerald-700">{(previewData.stats.openRate * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-emerald-800 font-semibold uppercase">Clicks</div>
                      <div className="text-sm font-bold text-blue-600">{previewData.stats.clicks.toLocaleString()}</div>
                    </div>
                  </div>
                )}

                {/* HTML Iframe Preview */}
                <div>
                  <div className="flex items-center justify-between text-xs font-semibold text-gray-700 mb-1.5">
                    <span>Rendered Email Template</span>
                  </div>
                  <div className="border border-gray-300 rounded-lg overflow-hidden h-[480px] bg-gray-100 shadow-inner">
                    <iframe
                      srcDoc={previewData.htmlContent || '<p style="padding:20px;color:#888;">No HTML content available</p>'}
                      title="Email Preview"
                      className="w-full h-full border-0"
                    />
                  </div>
                </div>

              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 text-xs">Failed to load preview details.</div>
            )}

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setPreviewCampaignId(null)}
                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Close Preview
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ─── MODAL 2: Add / Edit Slot Modal ─── */}
      {slotModalOpen && slotModalData && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg">
                  <Plus className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">
                    {slotModalData.existingSlot ? 'Edit Scheduled Slot' : 'Schedule New Campaign'}
                  </h3>
                  <p className="text-xs text-gray-500">{slotModalData.date} • {slotModalData.espName}</p>
                </div>
              </div>
              <button
                onClick={() => setSlotModalOpen(false)}
                className="p-1.5 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Form */}
            <div className="p-5 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Select Offer / Brand</label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full p-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  {candidates.map(c => (
                    <option key={c.campaignId} value={c.brandName}>
                      {c.brandName} ({c.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Text Edition</label>
                  <select
                    value={selectedEdition}
                    onChange={(e) => setSelectedEdition(e.target.value)}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="TA1">TA1 (Broad Comparison)</option>
                    <option value="TA2">TA2 (Refinansiering / Savings)</option>
                    <option value="TA3">TA3 (Punchy 1-CTA)</option>
                    <option value="TA4">TA4 (FAQ / Guidance)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Send Time</label>
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full p-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setSlotModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSlot}
                disabled={savingSlot}
                className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm disabled:opacity-50"
              >
                {savingSlot ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>

          </div>
        </div>
      )}

    </MainLayout>
  );
}
